import React, { useEffect, useRef, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';
import { useAppContext } from '../../app-core/AppContext';
import MiniFigma from '../../components/MiniFigma/MiniFigma';
import s from './NovaFigmaSheet.module.sass';

// Проекция момента из скилла apple-design: куда «долетит» жест
// при естественном затухании (exponential decay, как скролл iOS)
const project = (velocityPxPerSec, decelerationRate = 0.998) =>
    (velocityPxPerSec / 1000) * decelerationRate / (1 - decelerationRate);

// Rubber-band при попытке утащить лист выше упора
const rubberband = (overshoot, dimension, constant = 0.55) =>
    (overshoot * dimension * constant) / (dimension + constant * Math.abs(overshoot));

/**
 * NovaFigmaSheet — минифигма в iOS-шите: выезжает снизу почти до хедера.
 *
 * Память места: после первого открытия шит НЕ размонтируется — при закрытии
 * прячется через visibility, поэтому активная вкладка, позиция скролла и
 * раскрытые группы сохраняются между открытиями без каких-либо перерасчётов.
 * Вход/выход — transition по состоянию (а не keyframes), чтобы корректно
 * проигрываться при каждом повторном открытии.
 *
 * Свободное позиционирование: лист можно оставить на любой высоте — при
 * отпускании он остаётся там, куда «долетает» жест (с учётом инерции).
 * Утащили ниже порога закрытия — шит закрывается. Как только лист опущен
 * ниже верхнего упора, скрим гаснет и перестаёт перехватывать клики: с
 * приложением и админкой можно работать. Клик по админке опускает шит
 * под неё (z-index), клик по шиту поднимает обратно.
 */
// Ниже этого смещения (px) шит считается «опущенным»: скрим отключается
const LOWERED_AT = 64;

export default function NovaFigmaSheet({ open, onClose }) {
    const { siteSettings, currentUserGeneratedAmounts } = useAppContext();
    // Латч: не рендерим тяжёлый контент, пока шит ни разу не открывали
    const [everOpened, setEverOpened] = useState(open);
    // Видимое состояние отстаёт от open на кадр: при первом открытии элемент
    // должен успеть отрисоваться в закрытом положении, иначе transition
    // выезда не проигрывается и шит появляется рывком
    const [shown, setShown] = useState(false);
    // «Опущен» ли лист (скрим отключён) и текущее смещение якоря в px
    const [lowered, setLowered] = useState(false);
    const anchorOffsetRef = useRef(0);
    // В half-режиме клик по админке опускает шит под неё. Слой (z-index)
    // меняется сразу — админка мгновенно встаёт поверх, — а затемнение листа
    // плавно рисует его собственная вуаль (бэкдроп админки лежит ниже листа)
    const [behindAdmin, setBehindAdmin] = useState(false);
    // Слот в шапке шита, куда минифигма порталит язычки разделов
    const [tabsNode, setTabsNode] = useState(null);
    const sheetRef = useRef(null);
    const bodyRef = useRef(null);
    // История движений для вычисления velocity на отпускании
    const dragState = useRef(null);

    useEffect(() => {
        if (open) {
            setEverOpened(true);
            let raf2;
            const raf1 = requestAnimationFrame(() => {
                raf2 = requestAnimationFrame(() => setShown(true));
            });
            return () => {
                cancelAnimationFrame(raf1);
                if (raf2) cancelAnimationFrame(raf2);
            };
        }
        setShown(false);
    }, [open]);

    // Флаг «шит открыт» на корне документа: пока он стоит, админка не
    // закрывается по клику вне — закрытие идёт поэтапно (сначала шит,
    // следующим кликом — админка). Снимаем флаг отложенно (setTimeout):
    // React может отрендерить закрытие между обработчиками одного и того же
    // mousedown, и админка перестала бы видеть флаг в тот же клик
    useEffect(() => {
        if (open) {
            try { document.documentElement.setAttribute('data-figma-open', ''); } catch (_) {}
            return undefined;
        }
        const t = setTimeout(() => {
            try { document.documentElement.removeAttribute('data-figma-open'); } catch (_) {}
        }, 0);
        return () => clearTimeout(t);
    }, [open]);

    // Страховка: при размонтировании (например, смена темы) флаг снимается,
    // иначе админка навсегда перестала бы закрываться по клику вне
    useEffect(() => () => {
        try { document.documentElement.removeAttribute('data-figma-open'); } catch (_) {}
    }, []);

    // Единая точка закрытия: если лист стоит на inline-смещении (half или
    // после драга), сперва доводим его вниз с текущей позиции, иначе класс
    // просто «прыгнет» из-за приоритета inline-стиля
    const requestClose = useCallback(() => {
        const sheet = sheetRef.current;
        if (sheet && sheet.style.transform) {
            sheet.style.transition = 'transform 300ms cubic-bezier(0.32, 0.72, 0, 1)';
            sheet.style.transform = 'translateY(103%)';
            sheet.style.bottom = '';
            setTimeout(() => {
                sheet.style.transition = '';
                sheet.style.transform = '';
            }, 320);
        }
        anchorOffsetRef.current = 0;
        setLowered(false);
        setBehindAdmin(false);
        onClose();
    }, [onClose]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === 'Escape') requestClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, requestClose]);

    // Когда лист опущен, следим, с чем работает пользователь: клик по шиту
    // поднимает его наверх, клик по админке уводит под неё, а клик по самому
    // приложению (вне шита и админки) закрывает шит — как это делает админка
    useEffect(() => {
        if (!open || !lowered) return;
        const onDown = (e) => {
            const t = e.target;
            if (!(t instanceof Element)) return;
            if (t.closest('[data-figma-sheet]')) { setBehindAdmin(false); return; }
            if (t.closest('[class*="adminOverlay"]')) { setBehindAdmin(true); return; }
            // Оверлеи модалок и диалогов (в т.ч. открытых из админки)
            // «своими» кликами шит не закрывают
            if (t.closest('[class*="Modal"], [class*="globalModal"]')) return;
            // Клик по самому приложению закрывает шит всегда — галочка
            // «авто-скрытие» в админке касается только админки
            requestClose();
        };
        document.addEventListener('mousedown', onDown, true);
        return () => document.removeEventListener('mousedown', onDown, true);
    }, [open, lowered, requestClose]);

    const handlePointerDown = useCallback((e) => {
        const sheet = sheetRef.current;
        if (!sheet) return;
        e.currentTarget.setPointerCapture(e.pointerId);
        sheet.style.transition = 'none';
        const body = bodyRef.current;
        dragState.current = {
            startY: e.clientY,
            base: anchorOffsetRef.current,
            // Сколько контента скрыто за нижней кромкой — на столько можно
            // «растянуть» лист при оттягивании вверх, дальше пойдёт хвост
            reveal: body ? Math.max(0, body.scrollHeight - body.clientHeight - body.scrollTop) : 0,
            history: [{ y: e.clientY, t: performance.now() }],
        };
    }, []);

    const handlePointerMove = useCallback((e) => {
        const st = dragState.current;
        const sheet = sheetRef.current;
        if (!st || !sheet) return;
        const total = st.base + (e.clientY - st.startY);
        let offset;
        if (total < 0) {
            // Тянут выше упора: лист поднимается с сопротивлением. Пока за
            // нижней кромкой есть скрытый контент — лист растягивается
            // (низ прижат, контент дорисовывается). Когда показывать нечего,
            // подъём продолжается целиком, и снизу выезжает пустой «хвост»
            offset = rubberband(total, sheet.offsetHeight);
            const stretch = Math.min(-offset, st.reveal);
            sheet.style.bottom = `${-stretch}px`;
        } else {
            offset = total;
            sheet.style.bottom = '';
        }
        sheet.style.transform = `translateY(${offset}px)`;
        // Скрим гаснет/возвращается вживую прямо во время перетаскивания
        setLowered(offset > LOWERED_AT);
        st.history.push({ y: e.clientY, t: performance.now() });
        if (st.history.length > 6) st.history.shift();
    }, []);

    const handlePointerUp = useCallback(() => {
        const st = dragState.current;
        const sheet = sheetRef.current;
        dragState.current = null;
        if (!st || !sheet) return;

        // Velocity по последним точкам истории (px/s)
        const recent = st.history;
        const first = recent[0];
        const last = recent[recent.length - 1];
        const dt = Math.max(last.t - first.t, 1);
        const velocity = ((last.y - first.y) / dt) * 1000;

        const height = sheet.offsetHeight;
        // Ниже этой границы лист уже не «паркуем» — закрываем
        const closeThreshold = height * 0.8;
        // Куда «долетит» жест с учётом инерции — там и оставляем
        const projected = st.base + (last.y - st.startY) + project(velocity);

        if (projected > closeThreshold) {
            // Закрываем с текущей позиции
            sheet.style.transition = 'transform 300ms cubic-bezier(0.32, 0.72, 0, 1)';
            sheet.style.transform = 'translateY(103%)';
            sheet.style.bottom = '';
            anchorOffsetRef.current = 0;
            setLowered(false);
            setBehindAdmin(false);
            onClose();
            setTimeout(() => {
                sheet.style.transition = '';
                sheet.style.transform = '';
            }, 320);
            return;
        }

        // Свободная парковка: остаётся где отпустили (плюс инерция),
        // в пределах между верхним упором и порогом закрытия
        const target = Math.round(Math.max(0, Math.min(projected, closeThreshold)));
        // bottom возвращаем синхронно с transform — нижняя кромка при
        // возврате из «оттяжки вверх» остаётся прижатой к низу экрана
        sheet.style.transition = 'transform 380ms cubic-bezier(0.22, 1, 0.36, 1), bottom 380ms cubic-bezier(0.22, 1, 0.36, 1)';
        sheet.style.bottom = '';
        sheet.style.transform = target === 0 ? '' : `translateY(${target}px)`;
        anchorOffsetRef.current = target;
        setLowered(target > LOWERED_AT);
        if (target <= LOWERED_AT) setBehindAdmin(false);
    }, [onClose]);

    if (!everOpened) return null;

    const scrimClass = [
        s.scrim,
        shown ? s.scrimOpen : '',
        lowered ? s.scrimHalf : '',
        behindAdmin ? s.scrimBehind : '',
    ].filter(Boolean).join(' ');

    const node = (
        <div
            className={scrimClass}
            onMouseDown={(e) => { if (e.target === e.currentTarget) requestClose(); }}
            aria-hidden={!open}
            data-capture-exclude="true"
        >
            {/* data-figma-sheet — только на самом листе: клики по скриму
                (затемнённому приложению) для админки считаются «внешними» */}
            <div
                ref={sheetRef}
                className={s.sheet}
                data-figma-sheet
                role="dialog"
                aria-modal="true"
                aria-label="Шаблоны сообщений"
            >
                {/* Язычки разделов — над панелью, на её верхней кромке */}
                <div className={s.tabSlot} ref={setTabsNode} />
                <div className={s.panel}>
                    <div className={`${s.veil} ${behindAdmin ? s.veilOn : ''}`} aria-hidden="true" />
                    <div
                        className={s.grabberZone}
                        onPointerDown={handlePointerDown}
                        onPointerMove={handlePointerMove}
                        onPointerUp={handlePointerUp}
                        onPointerCancel={handlePointerUp}
                    >
                        <span className={s.grabber} aria-hidden="true" />
                    </div>
                    <button type="button" className={s.closeBtn} onClick={requestClose} aria-label="Закрыть">
                        <X size={16} strokeWidth={2.2} aria-hidden="true" />
                    </button>
                    <div className={s.body} ref={bodyRef}>
                        <MiniFigma
                            variant="nova"
                            siteSettings={siteSettings}
                            isNavbarCollapsed={false}
                            currentUserGeneratedAmounts={currentUserGeneratedAmounts}
                            tabsPortalNode={tabsNode}
                        />
                    </div>
                </div>
            </div>
        </div>
    );

    const root = typeof document !== 'undefined' ? document.querySelector('[data-nova-theme]') : null;
    return ReactDOM.createPortal(node, root || document.body);
}
